import { BillingApi, Configuration } from '@/api';
import { InvoiceFilters } from '@/api/BillingApi';
import { Invoices } from '@/models/Invoice';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState: Invoices = {
  totalCount: 0,
  estimatedTotalCount: 0,
  items: [],
};

export const invoicesQueryKey = ['invoices'] as const;

interface FindInvoicesQueryResult {
  invoices: Invoices;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindInvoicesQuery = (
  page: number,
  filters?: Partial<InvoiceFilters>,
): FindInvoicesQueryResult => {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const billingApi = new BillingApi(new Configuration(auth));

  const itemsPerPage = 10;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [...invoicesQueryKey, page, filters],
    queryFn: () => {
      return billingApi.listInvoices({
        subscriptionId: filters?.subscriptionId,
        status: filters?.status,
        limit: itemsPerPage,
        offset: page * itemsPerPage - itemsPerPage,
      });
    },
  });

  const invalidateQuery = async () => {
    return queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === invoicesQueryKey[0],
    });
  };

  return {
    invoices: data ?? initialState,
    isLoading,
    isFetching,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindInvoicesQuery;
